/**
 * The `kpi` plot (plots plan F): KPI figures in three layouts, one formatter.
 *
 * - `tile`: the deck's KPI tiles (exec-summary strip, rating slide), one
 *   figure over its sentence, through the existing `KpiTile`.
 * - `row`: a read per line (the Insights KPI rows): its context, base,
 *   sentence, per-asset columns and notes. The card composes the sentence and
 *   notes in the viewer's language from the typed parts Python stamped
 *   (`KpiItem.detail`) through `rowText`; the figure is always this plot's.
 * - `grid`: the scorecard, measures by tested items, each head-to-head row's
 *   winner drawn from Python's `topIds` / `tie`.
 *
 * Display only. Every figure, its decimals, the row order, the winner and the
 * low-base flag are Python's (`canonical_analysis/kpi_plot.py`); this component
 * never sorts, rounds or decides a winner.
 *
 * Headline views (decision 20): a card whose items carry `views` draws one
 * `StatTabs` row ("Average" | "Top N", or "NPS") over the rows or grid,
 * opening on `data.defaultView`. The active tab only picks which stamped view
 * each item prints (its figure, lanes, winner and cut); an item with no view
 * for the tab prints its own face. A payload without `views` (every stored
 * card, every deck tile) draws exactly as before. A tie draws no crown and says
 * "too close to call". Chrome comes from `data.labels` (or the caller's
 * overrides in the viewer's language); a chrome string with no label is
 * omitted, never filled in English.
 *
 * Plot code never imports the frontend's `@/lib`.
 */

import React, { useEffect, useState } from 'react'

import {
  KpiItem,
  KpiLabels,
  KpiProps,
  PlotFigure,
  PlotStatView,
} from '@digital-twins/slide-schemas/generated/plot-types'

import { KpiTile, KpiTileClamp } from '../../components/data-display/KpiTile/KpiTile'
import { PlotDataOf, PlotViewport } from '../contract'
import { FastTooltip } from '../_shared/FastTooltip'
import { StatTabs } from '../_shared/StatTabs'
import { formatKpiFigure } from './formatKpiFigure'

type KpiDataRead = Omit<PlotDataOf<'kpi'>, 'labels'> & { labels: Partial<KpiLabels> }

export interface KpiGridLane {
  id: string
  label: string
}

export interface KpiPlotProps {
  viewport?: PlotViewport
  data: KpiDataRead
  props?: KpiProps
  /** Chrome in the viewer's language, over `data.labels` (cards and survey tab). */
  labelOverrides?: Partial<KpiLabels>
  /** Test id root; every element's id extends it. */
  testIdPrefix?: string
  /** Tile layout: the text clamp the slide's schema declares. */
  tileClamp?: KpiTileClamp
  /** Tile layout: numeral ink (see `KpiTile.valueColor`). */
  tileValueColor?: string
  /** Grid layout: the tested items' letters, by lane id. */
  itemTags?: Record<string, string>
  /** Grid layout: the pooled block's one column heading. Omitted when absent. */
  scoreColumnLabel?: string
  /** Grid layout: the heading above the pooled block when both blocks draw. */
  overallHeading?: string
  /** Grid layout: the mark beside a lower-is-better measure. */
  renderLowerIsBetter?: (label: string) => React.ReactNode
  /**
   * Row layout: the row's words in the viewer's language (base line, sentence,
   * notes), composed by the card from the item's typed parts and the figure
   * this plot printed. Without it a row draws `plot_base_n` and `item.sentence`.
   */
  rowText?: (item: KpiItem, index: number, figure: string | null) => KpiRowText
  /** Row layout: a visual before the row's text (the card's share ring). */
  renderRowAside?: (item: KpiItem, index: number, figure: string | null) => React.ReactNode
  /** Row layout: a control after the sentence (the card's info tooltip). */
  renderRowAction?: (item: KpiItem, index: number) => React.ReactNode
  /** Controlled headline view; uncontrolled, the card opens on `data.defaultView`. */
  activeView?: PlotStatView
  onViewChange?: (view: PlotStatView) => void
}

/** One row's words, composed by the card (see `KpiPlotProps.rowText`). */
export interface KpiRowText {
  /** The base line under the context; null draws none. */
  base?: string | null
  /** The sentence; the card splices the printed figure in where its locale puts it. */
  sentence?: React.ReactNode
  /** Lines under the sentence, each with test id `${prefix}-${key}-${index}`. */
  notes?: Array<KpiRowNote>
}

export interface KpiRowNote {
  key: string
  text: string
  /** A footnote-sized line (the cohort). */
  small?: boolean
}

/** `{n}` / `{v}` placeholders filled with a figure Python stamped. */
function fillTemplate(template: string | undefined, values: Record<string, string | number>): string | null {
  if (!template) {
    return null
  }
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  )
}

function printed(figure: PlotFigure, locale: string | undefined, labels: Partial<KpiLabels>): string | null {
  return formatKpiFigure(figure, { locale, unitPpTemplate: labels.plot_unit_pp })
}

const ACCENT_CHIP_CLASS =
  'bg-[hsl(var(--report-sentiment-pos-bg))] text-[hsl(var(--report-sentiment-pos-text))]'
const WINNER_CHIP_CLASS = `rounded-full px-2.5 py-0.5 justify-self-center ${ACCENT_CHIP_CLASS}`
const STICKY_LABEL_CLASS = 'sticky left-0 z-10 bg-[hsl(var(--report-bg))]'

interface KpiHoverProps {
  isStatic: boolean
  label?: string | null
  children: React.ReactElement
}

function KpiHover(props: KpiHoverProps) {
  const { isStatic, label, children } = props
  if (isStatic || !label) {
    return children
  }
  return <FastTooltip label={label}>{children}</FastTooltip>
}

function DefaultLowerIsBetter(props: { label: string; testId: string; isStatic: boolean }) {
  const { label, testId, isStatic } = props

  const mark = (
    <span
      data-testid={testId}
      title={isStatic ? label : undefined}
      aria-label={label}
      className="ml-1 text-[10px] font-semibold text-muted-foreground align-middle"
    >
      ↓
    </span>
  )
  if (isStatic) {
    return mark
  }
  return <FastTooltip label={label}>{mark}</FastTooltip>
}

/**
 * Names the statistic where a bare figure would not say what it is: an NPS
 * read prints its score ("23"), which reads as NPS only with its name beside
 * it (owner decision 1). Omitted when the payload carries no label.
 */
function StatisticName(props: { item: KpiItem; labels: Partial<KpiLabels>; testId: string }) {
  const { item, labels, testId } = props
  const name = item.statistic === 'nps' ? labels.plot_nps_score : undefined

  if (!name) {
    return null
  }
  return (
    <span data-testid={testId} className="ml-1.5 text-[10px] text-muted-foreground">
      {name}
    </span>
  )
}

interface GridBlockProps {
  items: Array<KpiItem>
  lanes: Array<KpiGridLane>
  isHeadToHead: boolean
  highlightTop: boolean
  locale?: string
  labels: Partial<KpiLabels>
  testIdPrefix: string
  itemTags: Record<string, string>
  renderLowerIsBetter?: (label: string) => React.ReactNode
  isStatic: boolean
}

function GridBlock(props: GridBlockProps) {
  const { items, lanes, isHeadToHead, highlightTop, locale, labels, testIdPrefix, itemTags, isStatic } = props
  // Head-to-head stacks the measure name above the item columns so three
  // items fit the Insights column; a pooled block keeps the name beside its
  // one "Score" column. 56px is a value pill ("4.2/5" in the winner chip).
  const valueColumns = lanes.map(() => 'minmax(56px,1fr)').join(' ')
  const gridTemplateColumns = isHeadToHead ? valueColumns : `minmax(64px, 2.6fr) ${valueColumns}`
  const gapClass = isHeadToHead ? 'gap-x-1.5 gap-y-1' : 'gap-2'

  return (
    <div
      data-testid={`${testIdPrefix}-scroll`}
      className="flex flex-col overflow-x-auto pb-1 [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground/30"
    >
      <div className={`grid ${gapClass} min-w-fit`} style={{ gridTemplateColumns }}>
        {!isHeadToHead && <span className={STICKY_LABEL_CLASS} />}
        {lanes.map((lane) => {
          const tag = itemTags[lane.id]
          return (
            <span
              key={lane.id}
              data-testid={`${testIdPrefix}-column-${lane.id}`}
              className="flex flex-col items-center gap-1"
            >
              {tag && (
                <span
                  aria-hidden="true"
                  className={`inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-semibold leading-none ${ACCENT_CHIP_CLASS}`}
                >
                  {tag}
                </span>
              )}
              <KpiHover isStatic={isStatic} label={lane.label}>
                <span
                  title={isStatic ? lane.label : undefined}
                  className="text-[11px] font-medium text-muted-foreground text-center leading-tight break-words"
                >
                  {lane.label}
                </span>
              </KpiHover>
            </span>
          )
        })}
      </div>

      <div
        data-testid={`${testIdPrefix}-rows`}
        className={`flex flex-col min-w-fit ${isHeadToHead ? 'mt-4' : ''}`}
      >
        {items.map((item, index) => {
          const cells = isHeadToHead
            ? lanes.map((lane) => ({
                laneId: lane.id,
                figure: item.byLane?.find((value) => value.laneId === lane.id)?.figure ?? null,
              }))
            : [{ laneId: 'score', figure: item.figure }]
          const crowned = highlightTop && isHeadToHead && !item.tie ? new Set(item.topIds ?? []) : new Set<string>()
          const tieNote = isHeadToHead && item.tie ? labels.too_close_to_call : undefined
          const lowerIsBetter = item.figure.higherIsBetter === false

          return (
            <div key={item.id} data-testid={`${testIdPrefix}-row-${item.id}`}>
              {index > 0 && <div className="border-t border-border/50 my-2" />}
              <div className={`grid items-center ${gapClass}`} style={{ gridTemplateColumns }}>
                <KpiHover isStatic={isStatic} label={item.questionText || item.label}>
                  <span
                    title={isStatic ? (item.questionText || item.label) : undefined}
                    data-testid={`${testIdPrefix}-label-${item.id}`}
                    className={`text-xs text-muted-foreground leading-snug break-words ${STICKY_LABEL_CLASS}`}
                    style={isHeadToHead ? { gridColumn: '1 / -1' } : undefined}
                  >
                  {item.label}
                  <StatisticName item={item} labels={labels} testId={`${testIdPrefix}-statistic-${item.id}`} />
                  {lowerIsBetter &&
                    (props.renderLowerIsBetter ? (
                      props.renderLowerIsBetter(item.label)
                    ) : (
                      <DefaultLowerIsBetter label={item.label} testId={`${testIdPrefix}-lower-is-better`} isStatic={isStatic} />
                    ))}
                  {tieNote && (
                    <span
                      data-testid={`${testIdPrefix}-tie-${item.id}`}
                      className="ml-2 text-[10px] text-muted-foreground"
                    >
                      {tieNote}
                    </span>
                  )}
                </span>
                </KpiHover>
                {cells.map((cell) => {
                  const text = cell.figure ? printed(cell.figure, locale, labels) : null
                  const cellTestId = `${testIdPrefix}-cell-${item.id}-${cell.laneId}`
                  const isWinner = crowned.has(cell.laneId)
                  if (text == null) {
                    return (
                      <KpiHover key={cell.laneId} isStatic={isStatic} label={labels.plot_not_available}>
                        <span
                          data-testid={cellTestId}
                          title={isStatic ? labels.plot_not_available : undefined}
                          className="text-sm font-medium tabular-nums text-foreground text-center"
                        >
                          —
                        </span>
                      </KpiHover>
                    )
                  }
                  return (
                    <span
                      key={cell.laneId}
                      data-testid={cellTestId}
                      data-polarity-better={isWinner ? 'true' : undefined}
                      className={`text-sm font-medium tabular-nums text-center ${
                        isWinner ? WINNER_CHIP_CLASS : 'text-foreground'
                      }`}
                    >
                      {text}
                    </span>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface KpiRowProps {
  item: KpiItem
  index: number
  locale?: string
  labels: Partial<KpiLabels>
  showBase: NonNullable<KpiProps['showBase']>
  figureInSentence: NonNullable<KpiProps['figureInSentence']>
  testIdPrefix: string
  rowText?: KpiPlotProps['rowText']
  renderRowAside?: KpiPlotProps['renderRowAside']
  renderRowAction?: KpiPlotProps['renderRowAction']
  isStatic: boolean
}

/** A row's sentence without a card to compose it: the figure, then the item's sentence. */
function defaultSentence(
  item: KpiItem,
  figure: string | null,
  labels: Partial<KpiLabels>,
  figureInSentence: NonNullable<KpiProps['figureInSentence']>,
): string | null {
  if (figure == null) {
    return labels.plot_not_available ?? null
  }
  if (!item.sentence) {
    return figure
  }
  return figureInSentence === 'omit' ? item.sentence : `${figure} ${item.sentence}`
}

function KpiRow(props: KpiRowProps) {
  const { item, index, locale, labels, showBase, testIdPrefix, figureInSentence, isStatic } = props
  const figure = printed(item.figure, locale, labels)
  const lanes = item.byLane ?? []
  const defaultBase =
    showBase === 'row' && item.base != null ? fillTemplate(labels.plot_base_n, { n: item.base }) : null
  const words: KpiRowText = props.rowText
    ? props.rowText(item, index, figure)
    : { base: defaultBase, sentence: defaultSentence(item, figure, labels, figureInSentence) }
  const aside = props.renderRowAside ? props.renderRowAside(item, index, figure) : null
  const action = props.renderRowAction ? props.renderRowAction(item, index) : null
  const notes = words.notes ?? []

  return (
    <div data-testid={`${testIdPrefix}-row-${index}`} data-kpi-statistic={item.statistic ?? undefined}>
      {index > 0 && <div className="border-t border-border/50 my-3" />}
      <div className="flex items-start gap-3 min-w-0">
        {aside}
        <div className="flex flex-col gap-0.5 min-w-0 flex-1">
          {/* The context takes the text column's full width; the base is its
              own line under it, never a column beside it (a side column
              squeezed a long question in the narrow Insights card). */}
          <KpiHover isStatic={isStatic} label={item.questionText || undefined}>
            <span
              data-testid={`${testIdPrefix}-context-${index}`}
              title={isStatic ? (item.questionText || undefined) : undefined}
              className="text-sm min-w-0 leading-snug text-muted-foreground"
            >
            {item.label}
            <StatisticName item={item} labels={labels} testId={`${testIdPrefix}-statistic-${index}`} />
          </span>
          </KpiHover>
          {words.base ? (
            <span
              data-testid={`${testIdPrefix}-base-${index}`}
              className="text-[10px] tabular-nums text-muted-foreground"
            >
              {words.base}
            </span>
          ) : null}
          <div
            className={
              lanes.length > 0
                ? 'min-w-0 grid grid-cols-[minmax(0,1fr)_5rem] items-start gap-x-3 gap-y-1.5'
                : 'min-w-0 flex items-start gap-1.5'
            }
          >
            <span
              data-testid={`${testIdPrefix}-sentence-${index}`}
              className="text-sm min-w-0 font-semibold leading-snug text-foreground"
            >
              {words.sentence}
            </span>
            {action ? <div className="flex justify-end">{action}</div> : null}
            {lanes.length > 0 ? (
              <div data-testid={`${testIdPrefix}-assets-${index}`} className="contents">
                {lanes.map((lane) => (
                  <div key={lane.laneId} data-testid={`${testIdPrefix}-cell-${index}-${lane.laneId}`} className="contents">
                    <span className="text-sm min-w-0 leading-snug text-muted-foreground">{lane.label}</span>
                    <KpiHover isStatic={isStatic} label={printed(lane.figure, locale, labels) == null ? labels.plot_not_available : undefined}>
                      <span
                        title={isStatic && printed(lane.figure, locale, labels) == null ? labels.plot_not_available : undefined}
                        className="block text-right text-sm font-semibold tabular-nums leading-snug text-foreground"
                      >
                        {printed(lane.figure, locale, labels) ?? '—'}
                      </span>
                    </KpiHover>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          {notes.map((note) => (
            <span
              key={note.key}
              data-testid={`${testIdPrefix}-${note.key}-${index}`}
              className={`${note.small ? 'text-[11px]' : 'text-sm'} min-w-0 leading-snug text-muted-foreground`}
            >
              {note.text}
            </span>
          ))}
        </div>
      </div>
      <div data-testid={`${testIdPrefix}-row-segment`} className="min-h-4" aria-hidden="true" />
    </div>
  )
}

/**
 * The item as the active view prints it: that view's stamped figure,
 * statistic, lanes, winner and cut. An item with no such view keeps its face.
 * A lookup, never a computation.
 */
export function kpiItemInView(item: KpiItem, view: PlotStatView | null): KpiItem {
  const figures = view ? item.views?.[view] : undefined
  if (!figures) {
    return item
  }
  const { cut, ...detailRest } = item.detail ?? {}
  const detail = figures.cut ? { ...detailRest, cut: figures.cut } : detailRest
  return {
    ...item,
    figure: figures.figure,
    statistic: figures.statistic,
    ...(figures.byLane ? { byLane: figures.byLane, topIds: figures.topIds, tie: figures.tie } : {}),
    ...(item.detail || cut ? { detail } : {}),
  }
}

/**
 * The Top N tab's words: "Top 2", or "Top 2 / Top 3" on a card that mixes
 * box sizes (owner 2026-09-30), from each item's stamped `boxSize`.
 */
function topBoxesTabLabel(items: Array<KpiItem>, template: string | undefined): string | null {
  if (!template) {
    return null
  }
  const sizes = [...new Set(items.map((item) => item.views?.topBoxes?.boxSize).filter((n): n is number => n != null))]
  sizes.sort((a, b) => a - b)
  if (sizes.length === 0) {
    return null
  }
  return sizes.map((n) => template.replace('{n}', String(n))).join(' / ')
}

function lanesOf(items: Array<KpiItem>): Array<KpiGridLane> {
  const seen = new Map<string, string>()
  for (const item of items) {
    for (const lane of item.byLane ?? []) {
      if (!seen.has(lane.laneId)) {
        seen.set(lane.laneId, lane.label)
      }
    }
  }
  return [...seen.entries()].map(([id, label]) => ({ id, label }))
}

export function KpiPlot(props: KpiPlotProps) {
  const { data: stamped, labelOverrides, tileClamp, tileValueColor } = props
  const renderProps: KpiProps = props.props ?? {}
  const layout = renderProps.layout ?? 'tile'
  const locale = renderProps.locale
  const showBase = renderProps.showBase ?? 'none'
  const labels: Partial<KpiLabels> = { ...stamped.labels, ...(labelOverrides ?? {}) }
  const testIdPrefix = props.testIdPrefix ?? 'kpi-plot'
  const isStatic = renderProps.renderMode === 'static'

  const offeredViews = layout !== 'tile' && (stamped.views?.length ?? 0) >= 2 ? (stamped.views ?? []) : []
  const [ownView, setOwnView] = useState<PlotStatView | null>(stamped.defaultView ?? null)

  // A new payload (another card in the same slot) opens on its own default.
  useEffect(() => {
    setOwnView(stamped.defaultView ?? null)
  }, [stamped.defaultView])

  const requested = props.activeView ?? ownView
  const activeView = offeredViews.length > 0 && requested && offeredViews.includes(requested) ? requested : null
  const data =
    activeView != null ? { ...stamped, items: stamped.items.map((item) => kpiItemInView(item, activeView)) } : stamped

  const onViewChange = (view: PlotStatView) => {
    setOwnView(view)
    props.onViewChange?.(view)
  }

  const statTabs =
    offeredViews.length > 0 && activeView != null ? (
      <StatTabs
        views={offeredViews}
        activeView={activeView}
        labels={{
          average: labels.stat_tab_average,
          topBoxes: topBoxesTabLabel(stamped.items, labels.stat_tab_top_n),
          nps: labels.stat_tab_nps,
        }}
        onViewChange={onViewChange}
        testIdPrefix={`${testIdPrefix}-stat-tab-`}
      />
    ) : null

  const headerBase =
    showBase === 'header' && data.exhibitBase?.min != null
      ? fillTemplate(labels.plot_base_n, { n: data.exhibitBase.min })
      : null
  const header = headerBase ? (
    <span
      data-testid={`${testIdPrefix}-base`}
      className="text-[10px] text-muted-foreground tabular-nums self-end"
    >
      {headerBase}
    </span>
  ) : null

  if (layout === 'tile') {
    const clamp = tileClamp ?? { minLines: 1, maxLines: 3 }
    return (
      <div
        data-testid={testIdPrefix}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${data.items.length}, minmax(0, 1fr))`,
          gap: 'var(--ds-gap-xl)',
        }}
      >
        {data.items.map((item, index) => (
          <KpiTile
            key={item.id}
            completeText={props.viewport !== undefined}
            kpi={{
              value: item.figure.value ?? 0,
              valueFormat: 'count',
              unit: '',
              sentence: item.sentence ?? item.label,
            }}
            formattedValue={printed(item.figure, locale, labels) ?? labels.plot_not_available ?? ''}
            clamp={clamp}
            locale={locale}
            valueColor={tileValueColor}
            testId={`${testIdPrefix}-tile-${index}`}
            valueTestId={`${testIdPrefix}-value-${index}`}
            copyTestId={`${testIdPrefix}-copy-${index}`}
            sentenceTestId={`${testIdPrefix}-sentence-${index}`}
            lowBaseNote={item.lowBase ? labels.plot_low_base : undefined}
          />
        ))}
      </div>
    )
  }

  if (layout === 'row') {
    return (
      <div className="flex flex-col" data-testid={testIdPrefix} data-stat-view={activeView ?? undefined}>
        {statTabs ? <div className="mb-3">{statTabs}</div> : null}
        {header}
        {data.items.map((item, index) => (
          <KpiRow
            key={item.id}
            item={item}
            index={index}
            locale={locale}
            labels={labels}
            showBase={showBase}
            figureInSentence={renderProps.figureInSentence ?? 'prefix'}
            testIdPrefix={testIdPrefix}
            rowText={props.rowText}
            renderRowAside={props.renderRowAside}
            renderRowAction={props.renderRowAction}
            isStatic={isStatic}
          />
        ))}
      </div>
    )
  }

  const perItem = data.items.filter((item) => item.byLane != null)
  const pooled = data.items.filter((item) => item.byLane == null)
  const lanes = lanesOf(perItem)
  const highlightTop = (renderProps.highlight ?? 'top') === 'top'
  const itemTags = props.itemTags ?? {}
  const scoreLane: KpiGridLane = { id: 'score', label: props.scoreColumnLabel ?? '' }
  const isMixedScope = perItem.length > 0 && pooled.length > 0

  return (
    <div className="flex flex-col gap-3" data-testid={testIdPrefix} data-stat-view={activeView ?? undefined}>
      {statTabs}
      {header}
      {perItem.length > 0 && (
        <GridBlock
          items={perItem}
          lanes={lanes}
          isHeadToHead
          highlightTop={highlightTop}
          locale={locale}
          labels={labels}
          testIdPrefix={testIdPrefix}
          itemTags={itemTags}
          renderLowerIsBetter={props.renderLowerIsBetter}
          isStatic={isStatic}
        />
      )}
      {isMixedScope && props.overallHeading ? (
        <span data-testid={`${testIdPrefix}-overall-heading`} className="text-xs font-semibold text-muted-foreground">
          {props.overallHeading}
        </span>
      ) : null}
      {pooled.length > 0 && (
        <GridBlock
          items={pooled}
          lanes={[scoreLane]}
          isHeadToHead={false}
          highlightTop={false}
          locale={locale}
          labels={labels}
          testIdPrefix={testIdPrefix}
          itemTags={itemTags}
          renderLowerIsBetter={props.renderLowerIsBetter}
          isStatic={isStatic}
        />
      )}
    </div>
  )
}
